import type { DeviceCommand, DeviceCommandAck } from '@/types/player.types'
import { sanitizeLogText } from '@/utils/player-logger'

export interface CommandExecutorDeps {
  ack: (commandId: string, ack: DeviceCommandAck) => Promise<void>
  /**
   * Refaz o fetch do schedule (mesmo callback do `needsSync`). false = não há sync ativo para disparar (tela em
   * standby fora do horário de operação: o sync roda sozinho quando a tela volta).
   */
  refetch: () => boolean
  /** Reinício suave: recarrega a página do player. */
  reload: () => void
  clearCache: () => Promise<void>
  recentLogs: () => NonNullable<DeviceCommandAck['logs']>
}

/** Confirmações que falharam (rede) e são reenviadas no próximo heartbeat, no máximo estas. */
const MAX_PENDING_ACKS = 20
/** Espera antes da segunda tentativa de confirmar um `reload`. */
export const RELOAD_ACK_RETRY_MS = 2_000

/**
 * Executa os comandos remotos entregues no heartbeat e confirma cada um (Onda 2).
 *
 * - Cada comando roda uma vez: o id fica marcado, mesmo que o servidor entregue de novo.
 * - Confirmação que falha na rede vai para uma fila e é reenviada no próximo heartbeat.
 * - `reload` confirma ANTES de recarregar: depois do reload não há como confirmar.
 */
export const STANDBY_SYNC_ERROR = 'Tela fora do horário de operação; a agenda sincroniza quando ela voltar a tocar.'

export function createCommandExecutor(deps: CommandExecutorDeps) {
  const seen = new Set<string>()
  const pendingAcks: Array<{ id: string; ack: DeviceCommandAck }> = []

  async function send(id: string, ack: DeviceCommandAck): Promise<void> {
    try {
      await deps.ack(id, ack)
    } catch {
      if (pendingAcks.length < MAX_PENDING_ACKS) pendingAcks.push({ id, ack })
    }
  }

  async function execute(command: DeviceCommand): Promise<void> {
    switch (command.type) {
      case 'sync':
        if (deps.refetch()) await send(command.id, { status: 'succeeded' })
        else await send(command.id, { status: 'failed', error: STANDBY_SYNC_ERROR })
        return
      case 'clear_cache':
        await deps.clearCache()
        deps.refetch()
        await send(command.id, { status: 'succeeded' })
        return
      case 'upload_logs':
        await send(command.id, { status: 'succeeded', logs: deps.recentLogs() })
        return
      case 'reload':
        // A fila de reenvio vive em memória e morre com o reload: tenta confirmar duas vezes antes de recarregar.
        // Se as duas falharem, recarrega mesmo assim (o comando foi pedido) e ele aparece como "sem resposta".
        try {
          await deps.ack(command.id, { status: 'succeeded' })
        } catch {
          await new Promise((resolve) => setTimeout(resolve, RELOAD_ACK_RETRY_MS))
          await deps.ack(command.id, { status: 'succeeded' }).catch(() => undefined)
        }
        deps.reload()
        return
      default:
        // Tipo novo que este player ainda não conhece: responde em vez de deixar o comando sem resposta.
        await send((command as DeviceCommand).id, { status: 'failed', error: 'unsupported command' })
    }
  }

  return {
    async run(commands: DeviceCommand[]): Promise<void> {
      for (const command of commands) {
        if (seen.has(command.id)) continue
        seen.add(command.id)
        try {
          await execute(command)
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)
          await send(command.id, { status: 'failed', error: sanitizeLogText(message) })
        }
      }
    },

    async retryPendingAcks(): Promise<void> {
      const batch = pendingAcks.splice(0, pendingAcks.length)
      for (const { id, ack } of batch) await send(id, ack)
    }
  }
}
